import React, { useEffect, useRef, useState } from "react";
import { BadgeCheck, IdCard, Users } from "lucide-react";
import { api } from "../../lib/api";
import { FONT, GLASS, pill } from "./ui";
import { TILE_W } from "../../lib/iso";

// Pil kaca melayang di atas Balai Kota (mengikuti kamera peta tiap frame) → membuka Loket Kartu Warga Jawsel (KWJ).
// Posisi: puncak gedung = gridToScreen(pusat tapak, z); z dalam px dasar (zoom 1) ≈ 1.2 × lebar tile (3 lantai + kubah).
const TINGGI_BALAI = 1.2 * TILE_W;

export function PilBalaiKota({ engineRef, state, defs, cityId, terverifikasi, onClick }) {
  const ref = useRef(null);
  const [ada, setAda] = useState(false);
  // Jumlah Warga Asli kota ini (KWJ terverifikasi) — live, disegarkan tiap 20 dtk & saat tab kembali aktif
  const [wargaAsli, setWargaAsli] = useState(null);
  useEffect(() => {
    if (!cityId) return;
    let hidup = true;
    const cek = () => api.wargaAsliKota(cityId).then(d => { if (hidup) setWargaAsli(d.warga_asli ?? 0); }).catch(() => {});
    cek();
    const t = setInterval(cek, 20000);
    const vis = () => { if (document.visibilityState === "visible") cek(); };
    document.addEventListener("visibilitychange", vis);
    return () => { hidup = false; clearInterval(t); document.removeEventListener("visibilitychange", vis); };
  }, [cityId]);
  useEffect(() => {
    const def = (defs || []).find(d => d.bid === "balai_kota_agung");
    const b = (state?.buildings || []).find(x => x.bid === "balai_kota_agung");
    setAda(!!(def && b));
    if (!def || !b) return;
    let raf = 0;
    const cx = b.x + (def.w || 8) / 2, cy = b.y + (def.h || 8) / 2;
    const loop = () => {
      const E = engineRef.current, el = ref.current;
      if (E && el) {
        const [sx, sy] = E.gridToScreen(cx, cy, TINGGI_BALAI);
        const W = window.innerWidth, H = window.innerHeight;
        // skala relatif terhadap ukuran Balai Kota di layar (8 tile × TILE_W × zoom): pil ≈ 3/4 lebar gedung, min 34%, maks 75%
        const zoom = E.cam?.zoom || 1, skala = Math.max(0.34, Math.min(0.75, (8 * TILE_W * zoom * 0.64) / 260));
        const terlihat = 8 * TILE_W * zoom >= 60 && sx > -80 && sx < W + 80 && sy > -40 && sy < H + 40;   // sembunyi bila gedung < 60 px
        el.style.opacity = terlihat ? "1" : "0";
        el.style.pointerEvents = terlihat ? "auto" : "none";
        el.style.transform = `translate(${sx}px, ${sy}px) translate(-50%, -100%) scale(${skala.toFixed(3)})`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [engineRef, state, defs]);
  if (!ada) return null;
  return (
    <div ref={ref} data-testid="pil-balai-kota-wrap" style={{ position: "absolute", left: 0, top: 0, zIndex: 5, willChange: "transform", transformOrigin: "50% 100%", transition: "opacity .25s" }}>
      <style>{`@keyframes pilBalaiApung{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}`}</style>
      <div style={{ animation: "pilBalaiApung 2.8s ease-in-out infinite", display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* lencana Warga Asli live — di tengah, menumpang tepi atas pil */}
        {wargaAsli !== null && (
          <span data-testid="pil-balai-warga-asli" title={`${wargaAsli} Warga Asli terverifikasi di kota ini`}
            style={{ position: "relative", zIndex: 1, marginBottom: -13, display: "inline-flex", alignItems: "center", gap: 6, height: 26, padding: "0 12px 0 9px", borderRadius: 99,
              background: "#0000CD", color: "#fff", fontFamily: FONT, fontSize: 12.5, fontWeight: 800, letterSpacing: -0.2, border: "2.5px solid var(--g-bg-strong)", boxShadow: "0 6px 16px rgba(0,0,205,0.3)", whiteSpace: "nowrap" }}>
            <Users size={13} strokeWidth={2.6} /> {wargaAsli.toLocaleString("id-ID")} <span style={{ fontWeight: 600, opacity: .9 }}>Warga Asli</span>
            <span style={{ width: 7, height: 7, borderRadius: 99, background: "#7ee2ad", boxShadow: "0 0 0 2px rgba(126,226,173,0.35)", marginLeft: 2 }} />
          </span>
        )}
        <button data-testid="pil-balai-kota" onClick={onClick} aria-label={terverifikasi ? "Balai Warga — KWJ terverifikasi" : "Verifikasi Kartu Warga Jawsel"}
          style={{ ...pill, height: 60, padding: "0 22px 0 8px", gap: 12, cursor: "pointer", fontFamily: FONT, borderRadius: 99, border: "1px solid var(--g-border)", background: "var(--g-bg-strong)", boxShadow: "0 16px 40px rgba(0,0,205,0.22), var(--g-shadow)" }}>
          <span style={{ width: 44, height: 44, borderRadius: 99, background: terverifikasi ? "var(--c-green-bg)" : "#0000CD", color: terverifikasi ? "var(--c-green)" : "#fff", display: "grid", placeItems: "center", flex: "none" }}>
            {terverifikasi ? <BadgeCheck size={22} strokeWidth={2.4} /> : <IdCard size={22} strokeWidth={2.2} />}
          </span>
          <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.1, paddingTop: 4 }}>
            <span style={{ fontSize: 16.5, fontWeight: 800, letterSpacing: -0.3 }}>{terverifikasi ? "KWJ Terverifikasi" : "Verifikasi KWJ"}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: GLASS.sub }}>{terverifikasi ? "Buka Balai Warga" : "Kartu Warga Jawsel · Balai Kota"}</span>
          </span>
        </button>
        {/* ekor penunjuk ke gedung */}
        <span style={{ width: 0, height: 0, borderLeft: "8px solid transparent", borderRight: "8px solid transparent", borderTop: "9px solid var(--g-bg-strong)", filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.08))", marginTop: -1 }} />
      </div>
    </div>
  );
}
